import { forwardRef, useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { SHORTCUTS } from '@constants/shortcuts';
import { RAIL_PEEK_CLOSE_MS, RAIL_PEEK_DELAY_MS } from '@constants/timing';
import { useSlidingIndicator } from '@lib/hooks';
import { Icon, IconButton } from '@ui';
import type { NavGroup } from './nav';

/** Kementerian ESDM emblem (public/partners/esdm.svg). */
const EMBLEM = '/partners/esdm.svg';

interface Props {
  groups: NavGroup[];
  appName: string;
  home: string;
  /** labels shown and the page pushed aside (pinned open, a per-device preference); the drawer always shows them */
  expanded: boolean;
  drawer: boolean;
  /** drawer mode only */
  open: boolean;
  onToggleExpanded: () => void;
  onClose: () => void;
}

/**
 * Navy navigation rail: emblem with the open/close switch next to it, grouped icon links (a tooltip names each
 * one while collapsed) and one active bar that slides to the current page. Resting the mouse on the collapsed
 * rail opens it over the page while it stays there (hover-peek, the page does not move); the switch or `[` pins it
 * open. On phones and tablets the same rail is a modal drawer from the left.
 */
export const Rail = forwardRef<HTMLElement, Props>(function Rail({ groups, appName, home, expanded, drawer, open, onToggleExpanded, onClose }, ref) {
  const [peek, setPeek] = useState(false);
  const timer = useRef(0);
  const canPeek = !drawer && !expanded;
  const peeking = canPeek && peek;
  const wide = drawer || expanded || peeking;
  const nav = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const bar = useSlidingIndicator(nav, '.rail-link.active', `${pathname}|${wide}|${groups.length}`, 'y');

  // hover-peek opens only once a mouse rests on the rail (each move restarts the wait) and closes a moment after
  // it leaves (a diagonal exit across the edge does not flicker); never in drawer mode or while pinned open
  const closer = useRef(0);
  const canPeekNow = useRef(canPeek);
  canPeekNow.current = canPeek;
  const stopTimers = () => {
    window.clearTimeout(timer.current);
    window.clearTimeout(closer.current);
  };
  useEffect(() => stopTimers, []);
  useEffect(() => {
    if (canPeek) return;
    stopTimers();
    setPeek(false);
  }, [canPeek]);
  const onRest = (e: PointerEvent) => {
    if (!canPeekNow.current || e.pointerType !== 'mouse') return;
    window.clearTimeout(closer.current);
    if (peek) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => canPeekNow.current && setPeek(true), RAIL_PEEK_DELAY_MS);
  };
  const onLeave = () => {
    window.clearTimeout(timer.current);
    window.clearTimeout(closer.current);
    closer.current = window.setTimeout(() => setPeek(false), RAIL_PEEK_CLOSE_MS);
  };
  const toggle = () => {
    stopTimers();
    setPeek(false);
    onToggleExpanded();
  };
  const toggleLabel = expanded ? 'Ciutkan menu' : 'Bentangkan menu';

  let i = 0;
  return (
    <aside
      ref={ref}
      id="rail"
      className={`rail ${wide ? 'wide' : 'narrow'} ${peeking ? 'peek' : ''} ${drawer ? 'as-drawer' : ''} ${open ? 'open' : ''}`}
      aria-label="Navigasi"
      inert={drawer && !open}
      role={drawer ? 'dialog' : undefined}
      aria-modal={drawer && open ? true : undefined}
      onPointerEnter={onRest}
      onPointerMove={onRest}
      onPointerLeave={onLeave}
    >
      <div className="rail-top">
        <NavLink to={home} className="rail-brand" aria-label={`${appName}, ke beranda`}>
          <span className="rail-emblem">
            <img src={EMBLEM} alt="" width={36} height={36} decoding="async" />
          </span>
          {wide && <span className="rail-brand-name rail-in">{appName}</span>}
        </NavLink>
        {drawer ? (
          <IconButton icon="close" label="Tutup menu" className="rail-close" onClick={onClose} />
        ) : (
          <button type="button" className="rail-toggle" aria-label={toggleLabel} aria-expanded={expanded} aria-controls="rail" aria-keyshortcuts={SHORTCUTS.toggleRail} onClick={toggle}>
            <Icon name={expanded ? 'rail-close' : 'rail-open'} size={20} />
            <span className="rail-tip" aria-hidden="true">
              {toggleLabel} <kbd>{SHORTCUTS.toggleRail}</kbd>
            </span>
          </button>
        )}
      </div>
      <nav ref={nav} className="rail-nav" aria-label="Menu utama">
        <span ref={bar} className="rail-bar" aria-hidden="true" />
        {groups.map((g) => (
          <div key={g.id} className="rail-group" role="group" aria-labelledby={`rail-${g.id}`}>
            <span className="rail-group-label rail-in" id={`rail-${g.id}`}>{g.label}</span>
            {g.items.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `rail-link ${isActive ? 'active' : ''}`} style={{ '--i': i++ } as CSSProperties}>
                <Icon name={n.icon} size={20} />
                <span className="rail-label rail-in">{n.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      {/* the depot photo fades into the navy at the foot of the open rail (decorative, the same artwork as the login) */}
      {wide && <span className="rail-art" aria-hidden="true" />}
      {/* the shortcut lives at the foot of the open rail, so the app name keeps the room next to the emblem */}
      {wide && !drawer && (
        <p className="rail-hint rail-in" aria-hidden="true">
          <kbd className="rail-kbd">{SHORTCUTS.toggleRail}</kbd> {expanded ? 'ciutkan menu' : 'bentangkan menu'}
        </p>
      )}
    </aside>
  );
});
